import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Loader2, Trash2, Users } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Play's 12-tester, 14-day closed test — who has signed up for this app, and the links to share. */
export const TesterPanel = ({ app, onNotice }) => {
  const [data, setData] = useState(null);
  const [optIn, setOptIn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/mobile-apps/${app.id}/testers`);
      setData(res.data);
      setOptIn(res.data.opt_in_url || "");
    } catch (e) {
      setError(errMsg(e));
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, note) => {
    setBusy(true); setError("");
    try {
      await fn();
      await load();
      if (note) onNotice?.(note);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, note) => {
    navigator.clipboard?.writeText(text);
    onNotice?.(note);
  };

  if (!data) return null;
  const pct = Math.min(100, Math.round((data.count / data.target) * 100));
  const emails = data.testers.map((t) => t.email).join(", ");

  return (
    <div data-testid="tester-panel" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            <Users className="h-4 w-4 text-gold" /> Closed-test testers
          </p>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            A personal developer account needs {data.target} testers opted in for {data.days}
            {" "}continuous days before Play will take this app to production. Internal testing does
            not count towards it.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="tester-copy-link" variant="outline" size="sm"
            onClick={() => copy(data.signup_url, "Signup link copied.")}>
            <Link2 className="mr-2 h-4 w-4" /> Copy signup link
          </Button>
          {data.count > 0 && (
            <Button data-testid="tester-copy-emails" variant="outline" size="sm"
              onClick={() => copy(emails, `${data.count} email${data.count === 1 ? "" : "s"} copied.`)}>
              <Copy className="mr-2 h-4 w-4" /> Copy all emails
            </Button>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
          <div className="h-full rounded-full bg-gold transition-all duration-500"
            style={{ width: `${pct}%` }} />
        </div>
        <p data-testid="tester-progress" className="mt-2 text-sm text-slate-300">
          {data.count} of {data.target} signed up
          {data.count >= data.target
            ? " — enough to start the 14-day clock."
            : ` · ${data.target - data.count} to go.`}
        </p>
      </div>

      {error && <p data-testid="tester-panel-error" className="mt-3 text-sm text-red-400">{error}</p>}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="tester-optin-input" value={optIn} placeholder="https://play.google.com/apps/testing/…"
          onChange={(e) => setOptIn(e.target.value)} />
        <Button data-testid="tester-optin-save" disabled={busy} variant="outline"
          onClick={() => act(() => api.put(`/admin/mobile-apps/${app.id}/tester-opt-in`,
            { opt_in_url: optIn.trim() }), "Play test link saved.")}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save Play test link
        </Button>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Play Console → Testing → Closed testing → Testers tab → "Copy link". Signups see it the
        moment it is saved.
      </p>

      {data.count === 0 ? (
        <p data-testid="tester-empty" className="mt-5 text-sm text-slate-500">
          Nobody yet. Share the signup link — the page shows every app at once.
        </p>
      ) : (
        <ul data-testid="tester-list" className="mt-5 divide-y divide-ink-600">
          {data.testers.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-200">{t.name}</p>
                <p className="truncate font-mono text-[11px] text-slate-500">
                  {t.email}{t.device ? ` · ${t.device}` : ""}
                </p>
              </div>
              <Button data-testid={`tester-remove-${t.id}`} variant="ghost" size="sm" disabled={busy}
                onClick={() => act(() => api.delete(`/admin/testers/${t.id}`), "Tester removed.")}>
                <Trash2 className="h-4 w-4 text-slate-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
